import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { AuthService } from '../shared/auth.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';
import { phoneNumberValidator } from '../sign-up/sign-up.component';
import { Tel } from '../tel-input/tel-input.component';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    standalone: false
})
export class ProfileComponent implements OnInit {

  // only for data get, not set
  user: OperatorUser = LocalStorageService.current_user;

  // For profile updates
  profileForm = new FormGroup({
    firstName: new FormControl(this.user.firstName, [Validators.required, Validators.pattern('^[A-z0-9 ]+$')]),
    lastName: new FormControl(this.user.lastName, [Validators.required, Validators.pattern('^[A-z0-9 ]+$')]),
    email: new FormControl(this.user.email, [Validators.required, Validators.email]),
    licenseNum: new FormControl(this.user.licenseNumber, [Validators.minLength(5), Validators.maxLength(8), Validators.pattern('^([a-zA-Z]|[0-9])+$')]),
    phoneNum: new FormControl(new Tel('', '', ''), [Validators.required, phoneNumberValidator()]) //Use the tel control
  });

  updateSubscription!: Subscription;
  activeToast: ActiveToast<any> | undefined;

  constructor(
    public router: Router,
    private auth: AuthService,
    private toastr: ToastrService,
    private onlineStatusService: OnlineStatusService) {

  }

  ngOnInit(): void {
    if (this.auth.checkAccessToken().expired) {
      console.log("Access token expired, refreshing..");
      if (this.auth.checkRefreshToken().expired) {
        //Go to menu screen
        this.toastr.warning('Login to update your profile information', 'Sign-up session expired');
      } else {
        this.auth.postRefreshToken().subscribe((token: any) => {
          LocalStorageService.current_user.accessToken = token.accessToken;
          LocalStorageService.current_user.refreshToken = token.refreshToken;
          LocalStorageService.updateUser(false, true);
        });
      }
    }

    //In some instances, the user phone number may not be set
    this.profileForm.patchValue({
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.email,
      licenseNum: this.user.licenseNumber,
      phoneNum: this.user.phoneNumber ? new Tel(this.user.phoneNumber.slice(0, 3), this.user.phoneNumber.slice(3, 6), this.user.phoneNumber.slice(6, 10)) : new Tel('', '', '')
    });

    this.updateSubscription = this.profileForm.valueChanges.subscribe(() => this.updateProfile());

  }


  /**
 * Get data from the form controls and send an update with {@link MenuComponent.sendProfileUpdate}
 * @returns 
 */
  updateProfile() {
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this.toastr.warning('Internet connection is required to update profile information', 'Error');
      return;
    }

    if (this.auth.checkAccessToken().expired) {
      console.log("Access token expired, refreshing..");
      if (this.auth.checkRefreshToken().expired) {
        //Go to menu screen
        this.toastr.warning('Login to add and view your vessels', 'Sign-up session expired');
        this.router.navigate(['menu']);
      } else {
        this.auth.postRefreshToken().subscribe((token: any) => {
          LocalStorageService.current_user.accessToken = token.accessToken;
          LocalStorageService.current_user.refreshToken = token.refreshToken;
          LocalStorageService.updateUser(false, true).then(() => this.sendProfileUpdate())
        });
      }
    } else {
      this.sendProfileUpdate();
    }

  }


  /**
   * Format the profile data and send with {@link AuthService.postProfileUpdate}
   */
  sendProfileUpdate() {
    if (this.profileForm.valid) {
      let formValues = this.profileForm.value;
      let json = {
        "firstName": formValues.firstName,
        "lastName": formValues.lastName,
        "licenseNumber": formValues.licenseNum,
        "email": formValues.email,
        "phone": (formValues.phoneNum ?? '').toString(),
        "roles": [
          "User",
        ],
        "operatorUserId": this.user.operatorUserID
      };

      this.auth.postProfileUpdate(json).subscribe(_ => {
        if (this.activeToast) this.activeToast.toastRef.close(); 
        this.activeToast = this.toastr.success('Profile updated', 'Success!');
        
        //Update local storage and current user
        LocalStorageService.current_user.firstName = formValues.firstName!;
        LocalStorageService.current_user.lastName = formValues.lastName!;
        LocalStorageService.current_user.email = formValues.email!;
        LocalStorageService.current_user.licenseNumber = formValues.licenseNum!;
        LocalStorageService.current_user.phoneNumber = (formValues.phoneNum ?? '').toString();

        LocalStorageService.updateUser(true, false);
      }, err => {
        console.log(err);
        this.toastr.error(err.error.message ? err.error.message : err, 'Error');
      })
    }
  }


}
